<template>
  <div class="relative">
    <div
      class="flex w-full items-center rounded-md border border-neutral-300 bg-neutral-0 px-4 focus-within:border-secondary-500 focus-within:shadow-[0px_0px_4px_theme('colors.primary.500')] hover:border-secondary-500 active:border-secondary-500 disabled:border-neutral-400 disabled:bg-neutral-50"
      :class="[size === 'small' ? 'py-2.5' : '', size === 'medium' ? 'py-4' : '', size === 'large' ? 'py-4' : '']"
    >
      <div class="mr-3 flex items-center empty:hidden">
        <slot />
      </div>

      <input
        :id="labelId"
        ref="inputElement"
        :value="inputText"
        class="w-full border-none p-0 leading-3 text-neutral-800 outline-none transition-colors placeholder:text-neutral-300 focus:ring-0"
        :class="[
          size === 'small' ? 'h-4 text-xs' : '',
          size === 'medium' ? 'text-sm' : '',
          size === 'large' ? 'text-base' : ''
        ]"
        :placeholder="placeholder"
        :name="name"
        :disabled="disabled"
        :required="required"
        @input="onInput"
        @focus="openList()"
        @keydown.down.prevent="highlightNext()"
        @keydown.up.prevent="highlightPrev()"
        @keydown.enter.prevent="selectHighlighted()"
        @keydown.esc.prevent="closeList()"
        @blur="onBlur"
      />
    </div>

    <div
      v-if="showDropdown && filteredOptions.length"
      ref="listElement"
      class="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-neutral-200 bg-neutral-0 shadow-xs shadow-gray-300"
      role="listbox"
    >
      <button
        v-for="(opt, idx) in filteredOptions"
        :key="`opt-${opt.key}`"
        type="button"
        class="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-neutral-50 focus:bg-neutral-50"
        :class="[idx === highlightedIndex ? 'bg-neutral-50' : '', opt.persistent ? 'border-t border-neutral-100' : '']"
        role="option"
        :aria-selected="idx === highlightedIndex"
        @mousedown.prevent="selectOption(opt)"
      >
        {{ displayKey(opt.key) }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue';

import { FieldLabelIdInjectionKey } from './InfuseFieldInjection';

interface NormalizedOption {
  key: string;
  value: any;
  persistent?: boolean;
}

interface Props {
  modelValue: any;
  options: string[] | { key: string | number; value: any }[];
  name: string;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  format?: (value: any) => string;
  required?: boolean;
  disabled?: boolean;
  /** Options that stay visible at the bottom of the dropdown regardless of the search query (e.g. a "define new..." action). */
  persistentOptions?: { key: string | number; value: any }[];
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  size: 'medium',
  format: (value: string) => value
});

interface Emits {
  (e: 'update:modelValue', modelValue: any): void;
}

const emit = defineEmits<Emits>();

const focus = () => {
  inputElement.value?.focus();
};

defineExpose({ focus });

const labelId = inject(FieldLabelIdInjectionKey, null);

const inputElement = ref<InstanceType<typeof HTMLInputElement> | null>();
const listElement = ref<InstanceType<typeof HTMLDivElement> | null>();

const normalizedOptions = computed<NormalizedOption[]>(() => {
  if (Array.isArray(props.options)) {
    if (typeof props.options[0] === 'string' || typeof props.options[0] === 'number') {
      return (props.options as (string | number)[]).map((o) => ({ key: String(o), value: o }));
    }
    return (props.options as { key: string | number; value: any }[]).map((o) => ({
      key: String(o.key),
      value: o.value
    }));
  }
  return [];
});

const normalizedPersistentOptions = computed<NormalizedOption[]>(() =>
  (props.persistentOptions ?? []).map((o) => ({ key: String(o.key), value: o.value, persistent: true }))
);

const inputText = ref<string>('');
// what the user typed since opening the list; kept separate from inputText so a
// preselected value doesn't filter the list down to itself when the list opens
const query = ref<string>('');
const showDropdown = ref<boolean>(false);
const highlightedIndex = ref<number>(-1);

// keep inputText in sync when parent externally updates modelValue
watch(
  () => props.modelValue,
  (val) => {
    // try to map back to a key if value matches an option value
    const byValue = [...normalizedOptions.value, ...normalizedPersistentOptions.value].find((o) => o.value === val);
    inputText.value = byValue ? displayKey(byValue.key) : String(val ?? '');
  },
  { immediate: true }
);

const filteredOptions = computed<NormalizedOption[]>(() => {
  const q = query.value?.toString().toLowerCase() ?? '';
  const matches = q
    ? normalizedOptions.value.filter((o) => displayKey(o.key).toLowerCase().includes(q))
    : normalizedOptions.value;
  return [...matches, ...normalizedPersistentOptions.value];
});

function displayKey(key: string): string {
  return props.format?.(key) ?? key;
}

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  inputText.value = value;
  query.value = value;
  showDropdown.value = true;
  highlightedIndex.value = 0;
  // emit raw text so parents can validate/select accordingly while typing
  emit('update:modelValue', value);
}

function openList() {
  // show the full list until the user starts typing
  query.value = '';
  showDropdown.value = true;
  highlightedIndex.value = filteredOptions.value.length ? 0 : -1;
}

function closeList() {
  showDropdown.value = false;
  highlightedIndex.value = -1;
}

function onBlur() {
  // Delay closing so click selection can register
  setTimeout(() => closeList(), 100);
}

function highlightNext() {
  if (!filteredOptions.value.length) return;
  highlightedIndex.value = (highlightedIndex.value + 1) % filteredOptions.value.length;
  scrollHighlightedIntoView();
}

function highlightPrev() {
  if (!filteredOptions.value.length) return;
  highlightedIndex.value = (highlightedIndex.value - 1 + filteredOptions.value.length) % filteredOptions.value.length;
  scrollHighlightedIntoView();
}

function scrollHighlightedIntoView() {
  listElement.value?.children[highlightedIndex.value]?.scrollIntoView({ block: 'nearest' });
}

function selectHighlighted() {
  if (highlightedIndex.value < 0) return;
  selectOption(filteredOptions.value[highlightedIndex.value]);
}

function selectOption(opt: NormalizedOption) {
  inputText.value = displayKey(opt.key);
  emit('update:modelValue', opt.value);
  closeList();
}
</script>
